import {useState} from "react";
import {useNavigate} from "react-router-dom";
import useTranslate from "../../core/languages/hooks/useTranslate";
import useBookSave from "../../booking/hooks/useBookSave";
import useTransferPost from "../../transfers/hooks/useTransferPost";
import Transfer from "../../transfers/types/Transfer";
import {mapFormValuesToTransferFromPackageWizard} from "../../transfers/utils/mapFormValuesToTransfer";
import {TransferDirectionEnum} from "../../transfers/enums/TransferDirectionEnum";
import {SalesChannelEnum} from "../../common/enums/SalesChannelEnum";
import EventCity from "../../eventCities/types/EventCity";
import Prebooking from "../../booking/types/Prebooking";
import {WizardSaveInput} from "../types/WizardSaveInput";

interface UsePackageWizardSubmitProps {
  preBooking: Prebooking;
  eventCity: EventCity;
  selectedPaymentCardUuid?: string;
}

export const usePackageWizardSubmit = ({
                                         preBooking,
                                         eventCity,
                                         selectedPaymentCardUuid,
                                       }: UsePackageWizardSubmitProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const t = useTranslate();
  const navigate = useNavigate();
  const bookSave = useBookSave();
  const submitTransferData = useTransferPost();

  // Wrapper function to convert callback-based save to Promise
  const saveTransferAsPromise = (transfer: Transfer): Promise<void> => {
    return new Promise((resolve, reject) => {
      submitTransferData.save(transfer, {
        onSuccess: () => resolve(),
        onError: (error) => reject(error),
      });
    });
  };

  const onSubmit = async (data: WizardSaveInput) => {
    setIsSubmitting(true);
    setTransferError(null);

    // Process dial to extract iso2 and dialCode
    const dialParts = data.dial?.split("-") || [];
    // Process card expiration date
    const cardDate = data.cardDateMonth && data.cardDateYear
      ? `${data.cardDateMonth}/${data.cardDateYear}` : null;

    const singleMapped = mapFormValuesToTransferFromPackageWizard(
      data, data.direction!, SalesChannelEnum.PACKAGE, preBooking?.prebookingLanguage, eventCity
    );



    try {
      // Save main transfer and wait for completion
      await saveTransferAsPromise(singleMapped as Transfer);

      // Save departure transfer if needed
      if (data.direction === TransferDirectionEnum.ARRIVAL && data.departureTransport) {
        const departureOtherTransfer =
          mapFormValuesToTransferFromPackageWizard(
            data, TransferDirectionEnum.DEPARTURE, SalesChannelEnum.WEB, preBooking?.prebookingLanguage, eventCity
          );
        await saveTransferAsPromise(departureOtherTransfer as Transfer);
      }

      // Save arrival transfer if needed
      if (data.direction === TransferDirectionEnum.DEPARTURE && data.arrivalTransport) {
        const arrivalOtherTransfer =
          mapFormValuesToTransferFromPackageWizard(
            data, TransferDirectionEnum.ARRIVAL, SalesChannelEnum.WEB, preBooking?.prebookingLanguage, eventCity
          );
        await saveTransferAsPromise(arrivalOtherTransfer as Transfer);
      }

      const privacyPolicy = data.privacyPolicy ?? true;
      const contractTerms = data.contractTerms ?? true;

      // All transfers saved successfully, now save booking
      bookSave.save(
        {
          prebookingInternalId: preBooking?.internalId ?? null,
          name: data.name,
          surname: data.surname,
          email: data.email,
          iso2: dialParts[0],
          dialCode: Number(dialParts[1]),
          telephone: `${data.telephone}`,
          guests: data.guests,
          conditions: privacyPolicy && contractTerms,
          cardName: data.cardName,
          cardNumber: data.cardNumber,
          cardDate,
          paymentCard: selectedPaymentCardUuid
            ? "/web/payment_cards/" + selectedPaymentCardUuid
            : null,
          invoiceNameAndSurname: data.invoiceNameAndSurname,
          invoicePassportCif: data.invoicePassportCif,
          invoiceAddress: data.invoiceAddress,
          nationality: data.nationality,
          documentType: data.documentType,
        },
        {
          onSuccess: (prebooking: any) => {
            setIsSubmitting(false);
            navigate("/book-ok/" + prebooking.bookingInternalId);
          },
          onError: (error: any) => {
            setIsSubmitting(false);
            const errorMessage = error instanceof Error ? error.message : t("common.feedback.unexpected_error");
            setTransferError(errorMessage);
          },
        }
      );
    } catch (error) {
      // Transfer submission failed, don't proceed with booking
      setIsSubmitting(false);
      const errorMessage =
        error instanceof Error ? error.message : t("common.feedback.unexpected_error");
      setTransferError(errorMessage);
    }
  };

  return {
    onSubmit,
    isSubmitting,
    transferError,
    setTransferError,
  };
};
